/**
 * Thin wrapper around the voter_elections analytics endpoints shipped
 * by Agent A on 2026-04-11. The three endpoints live under
 * `/voters/analytics/*` in `backend/app/routers/voters.py`.
 *
 * Behavior is controlled by the build-time env var
 * `VITE_USE_MOCK_ANALYTICS`. When the value is the string `"1"` (or
 * the env var is unset), the wrapper returns an in-memory fixture
 * from `@/data/analyticsMocks` instead of making a network call. This
 * lets the React views iterate without hammering Postgres.
 *
 * To flip to live, set `VITE_USE_MOCK_ANALYTICS=0` in `.env` or pass
 * it to docker compose. The functions are pure read-only GETs and
 * resolve to the same shape regardless of source.
 *
 * Schemas are defined in `src/types/analytics.ts` and mirror the
 * Pydantic models in `backend/app/schemas/voter.py`. Any drift between
 * the two is a bug — fix backend first, mirror here second.
 */
import client from '@/api/client';
import {
  MOCK_DROP_OFF,
  MOCK_HEATMAP,
  MOCK_SPARKLINES,
} from '@/data/analyticsMocks';
import type {
  DropOffResponse,
  PrecinctSparklinesResponse,
  TurnoutHeatmapResponse,
} from '@/types/analytics';

// Default to MOCK ON until the user explicitly flips the env var.
function useMock(): boolean {
  const raw = (import.meta as any).env?.VITE_USE_MOCK_ANALYTICS;
  if (raw === undefined || raw === null || raw === '') return true;
  return raw !== '0' && raw !== 'false';
}

// Tiny delay so the loading state isn't an instant flash when running
// against fixtures — closer to what the live experience will feel like.
function delay<T>(value: T, ms = 250): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

// ---------------------------------------------------------------------------
// 1. Turnout heatmap — GET /voters/analytics/turnout-heatmap
// ---------------------------------------------------------------------------

export type FetchHeatmapOptions = {
  sinceYear?: number;
  topNPrecincts?: number;
  electionType?: string;
};

export async function fetchTurnoutHeatmap(
  opts: FetchHeatmapOptions = {},
): Promise<TurnoutHeatmapResponse> {
  if (useMock()) {
    return delay(MOCK_HEATMAP);
  }
  const params: Record<string, string | number> = {};
  if (opts.sinceYear !== undefined) params.since_year = opts.sinceYear;
  if (opts.topNPrecincts !== undefined) params.top_n_precincts = opts.topNPrecincts;
  if (opts.electionType) params.election_type = opts.electionType;
  const res = await client.get<TurnoutHeatmapResponse>(
    '/voters/analytics/turnout-heatmap',
    { params },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// 2. Drop-off detector — GET /voters/analytics/drop-off
// ---------------------------------------------------------------------------

export type FetchDropOffOptions = {
  baseYear: number;
  compareYear: number;
  electionType?: string;
  includeVoterSample?: boolean;
};

export async function fetchDropOff(
  opts: FetchDropOffOptions,
): Promise<DropOffResponse> {
  if (useMock()) {
    return delay(MOCK_DROP_OFF);
  }
  const params: Record<string, string | number | boolean> = {
    base_year: opts.baseYear,
    compare_year: opts.compareYear,
  };
  if (opts.electionType) params.election_type = opts.electionType;
  if (opts.includeVoterSample !== undefined)
    params.include_voter_sample = opts.includeVoterSample;
  const res = await client.get<DropOffResponse>(
    '/voters/analytics/drop-off',
    { params },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// 3. Precinct sparklines — GET /voters/analytics/precinct-sparklines
// ---------------------------------------------------------------------------

export type FetchSparklinesOptions = {
  lookback?: number;
  electionType?: string;
  topNPrecincts?: number;
};

export async function fetchPrecinctSparklines(
  opts: FetchSparklinesOptions = {},
): Promise<PrecinctSparklinesResponse> {
  if (useMock()) {
    return delay(MOCK_SPARKLINES);
  }
  const params: Record<string, string | number> = {};
  if (opts.lookback !== undefined) params.lookback = opts.lookback;
  if (opts.electionType) params.election_type = opts.electionType;
  if (opts.topNPrecincts !== undefined) params.top_n_precincts = opts.topNPrecincts;
  const res = await client.get<PrecinctSparklinesResponse>(
    '/voters/analytics/precinct-sparklines',
    { params },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// CSV helper used by the drop-off view's "Export" button
// ---------------------------------------------------------------------------

export function dropOffToCsv(resp: DropOffResponse): string {
  const headers = [
    'precinct_num',
    'precinct_name',
    'base_year',
    'compare_year',
    'election_type',
    'base_count',
    'retained_count',
    'dropped_count',
    'drop_off_pct',
  ];
  const escape = (v: unknown) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.join(',')];
  for (const p of resp.by_precinct) {
    lines.push(
      [
        p.precinct_num,
        p.precinct_name,
        resp.base_year,
        resp.compare_year,
        resp.election_type,
        p.base_count,
        p.retained_count,
        p.dropped_count,
        p.drop_off_pct,
      ]
        .map(escape)
        .join(','),
    );
  }
  return lines.join('\n');
}

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
